import { Transmit } from "@adonisjs/transmit-client";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

let transmitInstance: Transmit | null = null;

export function getTransmit(): Transmit {
  if (!transmitInstance) {
    transmitInstance = new Transmit({
      baseUrl: API_BASE_URL,
    });
  }
  return transmitInstance;
}

export interface OrderEventData {
  id: string;
  bookingCode: string;
  customerName: string;
  ticketName: string;
  participant: number;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  visitDate: string;
  createdAt: string;
}

export interface CheckInEventData {
  bookingCode: string;
  customerName: string;
  participant: number;
  ticketName: string;
  checkInAt: string;
}

export interface BookingStatusData {
  status: string;
  bookingCode: string;
  customerName: string;
  ticketName: string;
  participant: number;
  totalAmount: number;
  visitDate: string;
  checkInAt?: string;
}

export function subscribeToOrders(
  onNewOrder: (data: OrderEventData) => void,
  onStatusUpdate: (data: OrderEventData) => void
) {
  const transmit = getTransmit();

  const newOrderSub = transmit.subscription("orders/new");
  newOrderSub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: OrderEventData };
    if (msg.type === "new") {
      onNewOrder(msg.data);
    }
  });
  newOrderSub.create();

  const statusSub = transmit.subscription("orders/status");
  statusSub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: OrderEventData };
    if (msg.type === "status-update") {
      onStatusUpdate(msg.data);
    }
  });
  statusSub.create();

  return () => {
    newOrderSub.delete();
    statusSub.delete();
  };
}

export function subscribeToCheckIn(
  onCheckIn: (data: CheckInEventData) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription("checkin/today");
  sub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: CheckInEventData };
    if (msg.type === "checkin") {
      onCheckIn(msg.data);
    }
  });
  sub.create();

  return () => {
    sub.delete();
  };
}

export function subscribeToBookingStatus(
  bookingCode: string,
  onStatusChange: (data: BookingStatusData) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription(`booking/${bookingCode}`);
  sub.onMessage((data: unknown) => {
    onStatusChange(data as BookingStatusData);
  });
  sub.create();

  return () => {
    sub.delete();
  };
}

export interface WithdrawalEventData {
  id: string;
  partnerId: string;
  amount: number;
  status: string;
  adminNotes: string | null;
  processedAt: string | null;
}

export interface NotificationEventData {
  id: string;
  userId: string | null;
  targetRole: string | null;
  type: string;
  priority: "low" | "normal" | "high" | "urgent";
  title: string;
  message: string;
  actionUrl: string | null;
  actionLabel: string | null;
  referenceType: string | null;
  referenceId: string | null;
  metadata: Record<string, any> | null;
  icon: string | null;
  color: string | null;
  isRead: boolean;
  readAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * Subscribe to notifications for a specific user
 */
export function subscribeToUserNotifications(
  userId: string,
  onNotification: (data: NotificationEventData) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription(`notifications/user/${userId}`);
  sub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: NotificationEventData };
    if (msg.type === "new-notification") {
      onNotification(msg.data);
    }
  });
  sub.create();

  return () => {
    sub.delete();
  };
}

/**
 * Subscribe to notifications for a specific role
 */
export function subscribeToRoleNotifications(
  role: string,
  onNotification: (data: NotificationEventData) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription(`notifications/role/${role}`);
  sub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: NotificationEventData };
    if (msg.type === "new-notification") {
      onNotification(msg.data);
    }
  });
  sub.create();

  return () => {
    sub.delete();
  };
}

/**
 * Subscribe to partner's withdrawal updates (for partner portal)
 */
export function subscribeToWithdrawalUpdates(
  partnerId: string,
  onUpdate: (data: WithdrawalEventData) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription(`partner/${partnerId}/withdrawals`);
  sub.onMessage((message: unknown) => {
    const msg = message as { type: string; data: WithdrawalEventData };
    if (msg.type === "withdrawal-update") {
      onUpdate(msg.data);
    }
  });
  sub.create();

  return () => {
    sub.delete();
  };
}

/**
 * Subscribe to new withdrawal requests (for admin) - DEPRECATED: Use subscribeToRoleNotifications instead
 */
export function subscribeToNewWithdrawalRequests(
  onNewRequest: (data: WithdrawalEventData & { partnerName: string }) => void
) {
  const transmit = getTransmit();

  const sub = transmit.subscription("admin/withdrawals");
  sub.onMessage((message: unknown) => {
    const msg = message as {
      type: string;
      data: WithdrawalEventData & { partnerName: string };
    };
    if (msg.type === "new-request") {
      onNewRequest(msg.data);
    }
  });
  sub.create();

  return () => {
    sub.delete();
  };
}
